import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { useCreateCampaign } from '../api';
import { useFormContext } from 'react-hook-form';
import IconTikTok from 'assets/icon-tiktok.svg?react';
import { CopyAll } from '@mui/icons-material';
import copy from 'copy-to-clipboard';
import { useEnqueueSnackbar } from '../../../hooks';

const CreateCampaignButton: FC = () => {
  const { watch } = useFormContext();
  const accountId: string = watch('accountId');
  const createCampaign = useCreateCampaign();
  const { onShowAlert } = useEnqueueSnackbar();
  const handleCreateCampaign = useCallback(() => {
    createCampaign.mutate({
      ad_account_id: accountId,
    });
  }, [createCampaign, accountId]);

  const handleCloseAlert = useCallback(() => {
    createCampaign.reset();
  }, [createCampaign]);

  useEffect(() => {
    if (createCampaign.isError) {
      const timeoutId = setTimeout(() => {
        createCampaign.reset();
      }, 2000);

      return () => {
        clearTimeout(timeoutId);
      };
    }

    return () => null;
  }, [createCampaign]);

  const handleCopyCampaignId = useCallback((value: string) => {
    copy(value);
    onShowAlert(`Campaign ID: ${value} copied!`);
  }, [onShowAlert]);

  return (
    <>
      {createCampaign.isSuccess ? (
        <Box sx={{ position: 'relative' }}>
          <Alert icon={false}
                 severity="success"
                 sx={{ position: 'absolute', right: 0, width: '480px', bottom: 0, margin: 'auto' }}
                 onClose={handleCloseAlert}>
            <AlertTitle>Manual campaign created 🥷💰</AlertTitle>
            {createCampaign.data?.campaign_id && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
                <Typography
                  sx={t => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: t.palette.success.dark,
                  })}>Campaign ID: {createCampaign.data.campaign_id}</Typography>
                <IconButton
                  onClick={() => {
                    handleCopyCampaignId(createCampaign.data.campaign_id);
                  }}
                  size={'small'}
                  color={'success'}
                  sx={{ textTransform: 'initial', px: 1 }}
                >
                  <CopyAll />
                </IconButton>
              </Box>
            )}
          </Alert>
        </Box>
      ) : (
        <Button
          variant={'outlined'}
          color={createCampaign.isError ? 'error' : 'primary'}
          onClick={handleCreateCampaign}
          disabled={createCampaign.isPending}
          startIcon={
            createCampaign.isPending ? <CircularProgress color={'inherit'} size={18} /> :
              createCampaign.isError ? undefined : <IconTikTok width={20} height={24} />
          }
        >
          {createCampaign.isPending ? 'Creating campaign...' : createCampaign.isError ? 'API error occur...' : 'Create campaign in TikTok'}
        </Button>)}
    </>
  );
};

export default CreateCampaignButton;